/* eslint camelcase: 0 */
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import {
  LocalStorageKeys,
  availableLanguages,
  triplabotSettingAttributes,
  defaultProductionBaseURL,
  localeMapping,
  appID,
  appCode,
  maintenanceBaseURL,
  noticeWidgetID,
  getSearchWidgetUrl,
  getS3Url,
  SessionStorageKeys
} from 'javascript/Constants'
import { triplaServer } from 'src/apis/server'
import i18n from 'javascript/i18n'
import { Idp } from 'src/apis'
import { navigatorLanguage, guessBrowserLanguage, getIsFirstPartyDomainEnabled } from 'Utilities'
import ChatWidget from 'ChatWidget'
import { isWebView } from 'Utilities/appAccess'
import axios from 'axios'
import MaintenanceNoticeWidget from 'javascript/MaintenanceNoticeWidget'
import widgetDetector from 'javascript/helpers/widgetDetector'

class App {
  constructor() {
    this.scriptConfig = {
      dataAttrs: {}
    }
    this.triplabotSettings = {}
    this.maintenance = false
  }

  get isCustomBotTestMode() {
    return this.scriptConfig.dataAttrs['data-triplabot-mode'] === 'custom-bot-test'
  }

  init() {
    const ourScript = document.querySelector('script[data-triplabot-code]')
    if (!ourScript) return

    this.collectScriptDataAttrs(ourScript) // this must be the first setup method
    // need to call the following method right after the above method
    // to make sure we have a completely new CB environment for custom bot test
    this.setupCustomBotTest()

    this.fixDataInLocalStorage()
    this.generateInitialToken()
    this.render()
  }

  fixDataInLocalStorage() {
    if (!availableLanguages.includes(triplaLocalStorage.getItem(LocalStorageKeys.LOCALE))) {
      triplaLocalStorage.removeItem(LocalStorageKeys.LOCALE)
    }
  }

  collectScriptDataAttrs(scriptElement) {
    const params = {}
    const attrs = [...scriptElement.attributes]
    const scriptDataAttributes = {}

    attrs.forEach((attr) => {
      if (!/^data-/.test(attr.name)) return
      scriptDataAttributes[attr.name] = attr.value
    })

    this.scriptConfig.dataAttrs = scriptDataAttributes

    triplabotSettingAttributes.map((attribute) => {
      const value = scriptElement.getAttribute(attribute)
      switch (attribute) {
        case 'data-triplabot-code':
          params.code = value
          break
        case 'data-triplabot-base-url':
          params.base_url = value || defaultProductionBaseURL
          break
        case 'data-triplabot-restaurant-code':
          if (value) {
            sessionStorage.setItem(SessionStorageKeys.RESTAURENT_CODE, value)
          }
          break
        default:
      }
    })
    triplaServer.defaults.baseURL =
      process.env.NODE_ENV === 'development' || TARGET_ENV === 'devbuild'
        ? params.base_url
        : `${params.base_url.replace(/\/*(?:book)?\/*$/, '')}/chatbot/`
    this.scriptConfig = { ...this.scriptConfig, ...params }
  }

  generateInitialToken() {
    if (!triplaLocalStorage.getItem(LocalStorageKeys.CHAT_TOKEN)) {
      // generate 128-bit random string.
      const crypto = window.crypto || window.msCrypto
      triplaLocalStorage.setItem(
        LocalStorageKeys.CHAT_TOKEN,
        crypto.getRandomValues(new Uint32Array(4)).join()
      )
    }
  }

  render() {
    const hotelCode = isWebView ? appCode() : this.scriptConfig.code
    if (window.__triplabotCode__ === hotelCode) {
      return
    }
    window.__triplabotCode__ = hotelCode

    Promise.all([
      this.getTriplabotSettings(),
      Idp.getClientSession().catch(() => console.warn('Failed to fetch IDP'))
    ])
      .then(([settings, idpResponse]) => {
        this.triplabotSettings = settings
        if (idpResponse?.data?.client_session) {
          sessionStorage.setItem('triplabot_client_session', idpResponse.data.client_session)
        }

        if (
          getIsFirstPartyDomainEnabled(settings?.hotel_brand?.first_party_cookie_setting?.status)
        ) {
          const baseURL = `https://${settings?.hotel_brand?.first_party_cookie_setting?.domain_name}/chatbot/`
          this.scriptConfig.base_url = baseURL
          triplaServer.defaults.baseURL = baseURL
        }

        this.loadSearchbarScript()

        if (settings.polyglot_enabled) {
          triplaLocalStorage.setItem('browser_locale', navigatorLanguage())
        } else {
          triplaLocalStorage.removeItem('browser_locale')
        }

        const availableLanguages = settings.bot_languages
        let browserLocale = guessBrowserLanguage(navigatorLanguage())
        if (localeMapping[browserLocale]) {
          browserLocale = localeMapping[browserLocale]
        }

        if (
          !triplaLocalStorage.getItem('triplabot_locale') &&
          !availableLanguages.includes(browserLocale)
        ) {
          const defaultLocale = settings.default_language
          i18n.locale = defaultLocale
          triplaLocalStorage.setItem('triplabot_locale', defaultLocale)
        }

        if (!triplaLocalStorage.getItem('triplabot_locale')) {
          triplaLocalStorage.setItem('triplabot_locale', browserLocale)
        }

        this.appendDivWrapper()
        ChatWidget(
          this.scriptConfig,
          { ...this.triplabotSettings, maintenance: this.maintenance },
          appID
        )

        widgetDetector.setWidgetInitialized()
      })
      .catch((err) => {
        switch (err?.response?.status) {
          case 403:
          case 404:
            widgetDetector.hasTriplaWidgetPermission = false
            break
          default:
            console.error(err)
            break
        }
      })
  }

  getTriplabotSettings() {
    const code = isWebView ? appCode() : this.scriptConfig.code

    return triplaServer
      .get('api/triplabot/v1/chats/init', {
        params: {
          code,
          restaurant_code: sessionStorage.getItem(SessionStorageKeys.RESTAURENT_CODE) || undefined
        }
      })
      .then(({ data: json }) => {
        if (json.status === 'success') {
          return json
        } else {
          throw new Error('Something wrong with triplabot settings')
        }
      })
      .catch((err) => {
        console.error(err)
        const fallbackSettingURL = `${maintenanceBaseURL}maintenance/${code}/settings.json`

        return axios
          .get(fallbackSettingURL, {
            headers: { 'Content-Type': 'text/json' }
          })
          .then(({ data: json }) => {
            this.maintenance = true
            this.triplabotSettings = {
              ...json.hotel_config,
              active: json.hotel_config.chatbot_is_active,
              ...json
            }

            return this.triplabotSettings
          })
          .then((fallbackSettings) => {
            if (fallbackSettings.search_bar_is_active) {
              this.appendDivWrapper(noticeWidgetID)
              return MaintenanceNoticeWidget(fallbackSettings, noticeWidgetID)
            }
          })
      })
  }

  setupCustomBotTest() {
    const isCustomBotTestMode =
      this.scriptConfig.dataAttrs['data-triplabot-mode'] === 'custom-bot-test'
    if (!isCustomBotTestMode) return

    triplaLocalStorage.clear()
    const url = new URL(window.location)
    const locale = url.searchParams.get('triplabot_locale')

    if (locale) {
      triplaLocalStorage.setItem('triplabot_locale', locale)
      i18n.locale = locale
    }
  }

  loadSearchbarScript() {
    const isOnStandaloneBookingWidget =
      this.scriptConfig.dataAttrs['data-triplabot-mode'] === 'booking-widget'
    // We don't want to load the search bar if we are undergoing maintenance
    if (this.maintenance || isOnStandaloneBookingWidget) return

    axios.get(`${getS3Url()}SearchBar/manifest.json`).then((res) => {
      this.appendSearchBar(res.data['main.css'], res.data['main.js'])
    })
  }

  appendSearchBar(cssFileName, jsFileName) {
    const searchBarURL = getSearchWidgetUrl(jsFileName)
    const searchBarCSS = getSearchWidgetUrl(cssFileName)
    if (searchBarURL) {
      const script = document.createElement('script')
      script.src = searchBarURL
      document.body.appendChild(script)
    }

    if (searchBarCSS) {
      const linkCSS = document.createElement('link')
      linkCSS.rel = 'stylesheet'
      linkCSS.href = searchBarCSS
      document.head.appendChild(linkCSS)
    }
  }

  appendDivWrapper(id) {
    const divElm = document.createElement('div')
    divElm.id = id || appID
    document.body.appendChild(divElm)
  }
}

export default App
